// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import AppsUtil from '../../../utils/AppsUtil';
import CompatibilityUtil from '../../../utils/CompatibilityUtil';
import React, {Component} from 'react';
import './ProductDescCard.scss';
import Button from '@react/react-spectrum/Button';
import {productDescCardDetails} from '../appPickerConstants';
import uuid from 'uuid';
import AppsAnalytics from '../../../analytics/AppsAnalytics';

type Props = {
	interestCategory: string,
	getPlatformAppInfo: Function,
	history: Object,
	intl: Object,
	getAppBtnType: Function,
	app: string,
	getAppPlatform: Function,
	environment: string
};

export default class ProductDescCard extends Component<Props> {
	sapCode: string;
	productConfig: Object

	constructor(props: Props) {
		super(props);
		this.sapCode = this.props.getPlatformAppInfo(this.props.app).sapCode;
		this.productConfig = AppsUtil.getFFCProductConfigBySapCode(this.sapCode);
	}

	componentDidMount() {
		this.sendAnalytic('render', 'app');
	}

	componentWillUpdate(nextProps: Object) {
		if (this.props.interestCategory !== nextProps.interestCategory) {
			this.sapCode = this.props.getPlatformAppInfo(nextProps.app).sapCode;
			this.productConfig = AppsUtil.getFFCProductConfigBySapCode(this.sapCode);
		}
	}

	componentDidUpdate(prevProps: Object) {
		if (this.props.interestCategory !== prevProps.interestCategory) {
			this.sendAnalytic('render', 'app');
		}
	}

	sendAnalytic(type: string, subType: string, extURL: ?string) {
		const appsAnalytics = new AppsAnalytics(this.props.environment);
		appsAnalytics.initAnalytics();
		const appConfig = {
			sapCode: this.sapCode,
			displayName: this.productConfig.displayName,
			filter: this.props.interestCategory,
			extURL,
			entitled: AppsUtil.isAppEntitled(this.sapCode, this.props.getAppPlatform(this.productConfig))
		};
		appsAnalytics.genericAnalyticEvent(appConfig, type, subType, undefined, undefined, undefined, 'recommended', 'Guide Me', 'App Picker');
	}

	onButtonClick(buttonType: string) {
		if (buttonType.includes('get')) {
			const branchURLTag = '~tags=PhotographyPlan';
			const extURL = AppsUtil.openExternalLink(this.productConfig.mobileAppUrl, this.sapCode, uuid.v4(), undefined, branchURLTag);
			this.sendAnalytic('click', 'get', extURL);
		}
		else {
			// TODO: send link wont work correctly on desktop
			this.sendAnalytic('click', 'send-link');
			this.props.history.push(`/apps/all/desktop/pdp/${AppsUtil.getProductIdFromSapcode(this.sapCode)}?action=install&source=appPicker`);
		}
	}

	// $FlowFixMe
	get tagLine() {
		return this.props.intl.formatMessage({id: productDescCardDetails[this.props.app].tagLine[this.props.interestCategory]});
	}

	get description() {
		return this.props.intl.formatMessage({id: productDescCardDetails[this.props.app].desc[this.props.interestCategory]}, {appName: this.productConfig.displayName});
	}

	renderDownloadButton() {
		if (CompatibilityUtil.isAppSupported(this.sapCode) || CompatibilityUtil.isAnyOlderVersionSupported(this.sapCode) || AppsUtil.isMobileOrTab()) {
			let buttonName;
			let buttonType;
			if (AppsUtil.isMobileOrTab()) {
				buttonType = this.props.getAppBtnType(this.props.app);
				if (buttonType === 'get') {
					if (AppsUtil.isIPad()) {
						buttonType = 'getforipad';
					}
				}
				buttonName = this.props.intl.formatMessage({id: `apps.tile.${buttonType}`});
			}
			else {
				buttonName = this.props.intl.formatMessage({id: 'apps.tile.download'});
				buttonType = 'download';
			}
			return <Button className='productCTA' variant='cta' onClick={() => {this.onButtonClick(buttonType);}}
				aria-label={`${buttonName} ${this.productConfig.displayName}`} label={buttonName}
			/>;
		}
		return null;
	}

	render() {
		return (
			<div className='ProductDescCard'>
				<div
					className='productDescCardImg'
					style={{backgroundImage: `url(${productDescCardDetails[this.props.app].cardImageSrc})`}}
				/>
				<div className='productDescription'>
					<span className='productTagLine'>{this.tagLine}</span>
					<div className='product-header'>
						<img src={AppsUtil.getProductIcon(this.productConfig)} className='product-logo' alt={this.productConfig.displayName} />
						<div className='productName'>{this.productConfig.displayName}</div>
					</div>
					<p className='productDesc'>{this.description}</p>
					{this.renderDownloadButton()}
				</div>
			</div>
		);
	}
}